import * as React from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  useTheme,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { Add, Edit, HelpOutline } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import GeneralLoading from "../../../shared-components/Loading";
import { useApiClient } from "../../../api";
import { InformationSnippet } from "../../../shared-components/InformationSnippet";
import StatBox from "../../../shared-components/StatBox";
import QuestionRating from "../components/QuestionRating";
import { CreateQuestionDialog } from "../components/CreateQuestionDialog";
import Tooltip from "@mui/material/Tooltip";
import { AddTagsDialog } from "../../../shared-components/ExpandableTagsBox";
import { withPageTracking } from "../../../services/postHog";
import { QuestionViewCard } from "../components/QuestionViewCard";
import { AvailableQuestionTypes } from "../components/QuestionTypePicker";

export const QuestionViewSummary = ({
  question,
  onReviewSubmitted,
  onAddTagsClicked,
}) => {
  const navigate = useNavigate();

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2}>
            <Typography variant="h3">Question information</Typography>
            {question.isPublished ? (
              <Box sx={{ position: "relative", top: 2 }}>
                <QuestionRating
                  question={question}
                  includeCount
                  onSubmitted={onReviewSubmitted}
                />
              </Box>
            ) : (
              <Chip label={"Draft"} />
            )}
          </Stack>
          {question.questionType ===
            AvailableQuestionTypes.LongText.backendName && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip
                title={
                  !question.isComplete &&
                  "Question must be complete before using in an assignment"
                }
              >
                <span>
                  <Button
                    disabled={!question.isComplete}
                    startIcon={<Add />}
                    variant="contained"
                    onClick={() =>
                      navigate(
                        `/assignments/create/new?questionId=${question.id}`,
                      )
                    }
                  >
                    Create Assignment
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Stack>

        <Grid container spacing={2} direction={"row"} sx={{ mt: 2, mb: 2 }}>
          <Grid size={4}>
            <InformationSnippet
              title={"Subject"}
              value={question.subject.name}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Education Level"}
              value={question.educationLevel.level}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Tags"}
              value={question.tags}
              useTagBox
              allowAddTags
              onAddTagsClicked={onAddTagsClicked}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export const BestAnswerCard = ({ answer }) => {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [textMaxWidth, setTextMaxWidth] = useState("100%");

  useEffect(() => {
    if (!cardRef.current) return;

    const updateWidth = () => {
      const cardWidth = cardRef.current.clientWidth;
      setTextMaxWidth(cardWidth * 0.8); // 80% of card width
    };

    updateWidth(); // initial measurement

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(cardRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <Card ref={cardRef} variant="background" sx={{ mt: 2 }}>
      <CardContent>
        <Typography
          variant="body1"
          sx={{
            maxWidth: textMaxWidth,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {answer.answer}
        </Typography>
        <Button
          variant={"outlined"}
          sx={{
            mt: 2,
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          onClick={() =>
            navigate(`/assignments/answer/view/anonymous/${answer.id}`)
          }
        >
          View
        </Button>
      </CardContent>
    </Card>
  );
};

export const QuestionBestAnswers = ({ answers }) => {
  const theme = useTheme();
  const NoAnswers = () => {
    return (
      <Card sx={{ backgroundColor: theme.palette.background.default, mt: 2 }}>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            minHeight: 150,
          }}
        >
          <HelpOutline fontSize={"large"} />
          <Typography variant={"h5"}>No best answers yet</Typography>
          <Typography variant={"subtitle1"}>
            Answers with the top scores will be shown here
          </Typography>
        </CardContent>
      </Card>
    );
  };
  return (
    <Card>
      <CardContent>
        <Typography variant={"h6"}>Best Answers</Typography>

        {answers.length > 0 ? (
          answers.map((answer) => (
            <BestAnswerCard key={answer.id} answer={answer} />
          ))
        ) : (
          <NoAnswers />
        )}
      </CardContent>
    </Card>
  );
};

const QuestionView = () => {
  const { id } = useParams();
  const ApiClient = useApiClient();
  const [question, setQuestion] = useState({});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [addTagsOpen, setAddTagsOpen] = useState(false);
  const [newTags, setNewTags] = useState([]);
  const [savingTags, setSavingTags] = useState(false);

  useEffect(() => {
    fetchQuestion();
  }, [id]);

  const fetchQuestion = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.question.getQuestion({
        question: id,
      });
      if (response) {
        setQuestion(response.question);
      }
    } catch (error) {
      console.error("Failed to fetch question", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <GeneralLoading />;
  }

  if (!question || !Object.keys(question).length) {
    return <Typography>Unable to load question data</Typography>;
  }

  const hasAverageMark =
    question.averageMark !== null &&
    question.averageMark !== undefined &&
    question.averageMark !== "" &&
    Number.isFinite(Number(question.averageMark));

  const handleAddTags = async () => {
    if (!newTags || newTags.length === 0) {
      setAddTagsOpen(false);
      return;
    }
    setSavingTags(true);
    try {
      await ApiClient.question.addQuestionTags({
        question: question.id,
        tags: newTags,
      });
      await fetchQuestion();
      setAddTagsOpen(false);
    } catch (e) {
      console.error("Failed to add tags", e);
    } finally {
      setSavingTags(false);
    }
  };

  return (
    <FruitifyContainer>
      <AddTagsDialog
        open={addTagsOpen}
        onClose={() => setAddTagsOpen(false)}
        tags={question.tags}
        subject={question.subject}
        educationLevel={question.educationLevel}
        newTags={newTags}
        setNewTags={setNewTags}
        onSave={handleAddTags}
        saving={savingTags}
      />
      <CreateQuestionDialog
        open={open}
        setOpen={setOpen}
        forYearGroup={question?.yearGroup}
        forSubject={question?.subject}
        forEducationLevel={question?.educationLevel}
        createdQuestion={question}
        setCreatedQuestion={() => fetchQuestion()}
        onComplete={() => fetchQuestion()}
      />
      <QuestionViewSummary
        question={question}
        onReviewSubmitted={() => fetchQuestion()}
        onAddTagsClicked={() => setAddTagsOpen(true)}
      />
      <QuestionViewCard
        question={question}
        sx={{ mt: 2 }}
        hideMarkScheme={
          question.questionType !== AvailableQuestionTypes.LongText.backendName
        }
        onImagesChanged={() => fetchQuestion()}
      />
      {question.isPublished ? (
        <>
          <Grid
            container
            spacing={4}
            direction={"row"}
            sx={{ mt: 2, mb: 2 }}
            alignItems={"center"}
          >
            <Grid size={{ xs: 12, md: 4 }}>
              <StatBox title={"Times used"} stat={question.popularity} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <StatBox
                title={"Students answered"}
                stat={question.timesAnswered}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <StatBox
                title={"Average Mark"}
                stat={hasAverageMark ? `${question.averageMark} %` : undefined}
                noDataMessage={hasAverageMark ? undefined : "No answers yet"}
              />
            </Grid>
          </Grid>

          <QuestionBestAnswers answers={question.bestAnswers} />
        </>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Button
            variant={"contained"}
            startIcon={<Edit />}
            onClick={() => setOpen(true)}
          >
            Edit
          </Button>
        </Box>
      )}
    </FruitifyContainer>
  );
};

export default withPageTracking(QuestionView, { feature: "question" });
